import {
    Attempt,
    AttemptReason,
    Carrier,
    CarrierContact,
    CarrierRequest,
    Policy,
    PolicyComment,
    PolicyQueueRow,
    PolicyValue,
    Portfolio,
    PremiumPayment,
    RawPolicyQueueRowData,
    ServicingUser
} from "./PolicyQueueTypes";
import { PolicyAction, defaultPolicyValue, defaultPremiumPayment, subscription } from "./Constants";
import { Post, Switchboard } from "@itm21st/switchboard";
import { ReactNode, createContext, useCallback, useEffect, useState } from "react";
import { notImplemented, parseDates } from "../../services/Library";

import { DropdownItemProps } from "semantic-ui-react";
import { SortDescriptor } from "@progress/kendo-data-query";
import { orderBy as lodashOrderBy } from "lodash";
import { toast } from "react-toastify";
import useDatabroker from "../../hooks/useDatabroker";
import useSwitchboard from "../../hooks/useSwitchboard";

export interface PolicyQueueContextProps {
    activeIndex: number;
    attemptHistory: Attempt[];
    attemptReasons: AttemptReason[];
    attemptReasonOptions: DropdownItemProps[];
    bulkSearchActive: boolean;
    bulkSearchCategory: string;
    bulkSearchInput: string;
    carrierContacts: CarrierContact[] | undefined;
    carrierRequests: CarrierRequest[] | undefined;
    currentPolicyValue: PolicyValue;
    currentPremiumPayment: PremiumPayment;
    comments: PolicyComment[];
    excelLoading: boolean;
    getPolicyDetail: (policyID: number) => Promise<void>;
    gridData: PolicyQueueRow[];
    handleSubmitPayment: (post: Post<PremiumPayment>) => void;
    handleSubmitValue: (post: Post<PolicyValue>) => void;
    isAssignModalOpen: boolean;
    isAttemptModalOpen: boolean;
    isCarrierContactsModalOpen: boolean;
    isCommentModalOpen: boolean;
    policyDetail: Policy | undefined;
    policyDetailLoading: boolean;
    policyQueueData: PolicyQueueRow[] | undefined;
    policyQueueLoading: boolean;
    policyValueHistory: PolicyValue[] | undefined;
    premiumPaymentHistory: PremiumPayment[] | undefined;
    refreshCarrierRequests: (policyID: number) => void;
    selectedCarrierRequest: CarrierRequest | undefined;
    selectedItems: PolicyQueueRow[] | undefined;
    selectedPremiumPaymentID: number | undefined;
    servicingUserOptions: DropdownItemProps[];
    servicingUsers: ServicingUser[];
    setActiveIndex: (data: number) => void;
    setAttemptHistory: (data: Attempt[]) => void;
    setAttemptReasons: (data: AttemptReason[]) => void;
    setAttemptReasonOptions: (data: DropdownItemProps[]) => void;
    setBulkSearchActive: (data: boolean) => void;
    setBulkSearchCategory: (data: string) => void;
    setBulkSearchInput: (data: string) => void;
    setCarrierContacts: (data: CarrierContact[] | undefined) => void;
    setCarrierRequests: (data: CarrierRequest[] | undefined) => void;
    setComments: (data: PolicyComment[]) => void;
    setCurrentPolicyValue: (data: PolicyValue) => void;
    setCurrentPremiumPayment: (data: PremiumPayment) => void;
    setExcelLoading: (data: boolean) => void;
    setGridData: (data: PolicyQueueRow[]) => void;
    setIsAssignModalOpen: (data: boolean) => void;
    setIsAttemptModalOpen: (data: boolean) => void;
    setIsCarrierContactsModalOpen: (data: boolean) => void;
    setIsCommentModalOpen: (data: boolean) => void;
    setPolicyDetail: (data: Policy) => void;
    setPolicyDetailLoading: (data: boolean) => void;
    setPolicyQueueData: (data: PolicyQueueRow[]) => void;
    setPolicyQueueLoading: (data: boolean) => void;
    setPolicyValueHistory: (data: PolicyValue[] | undefined) => void;
    setPremiumPaymentHistory: (data: PremiumPayment[] | undefined) => void;
    setSelectedCarrierRequest: (data: CarrierRequest | undefined) => void;
    setSelectedItems: (data: PolicyQueueRow[] | undefined) => void;
    setSelectedPremiumPaymentID: (data: number | undefined) => void;
    setServicingUserOptions: (data: DropdownItemProps[]) => void;
    setServicingUsers: (data: ServicingUser[]) => void;
    setSort: (data: SortDescriptor[]) => void;
    setTextSearch: (data: string) => void;
    sort: SortDescriptor[];
    switchboard: Switchboard | undefined;
    textSearch: string;
}

export const PolicyQueueContext = createContext<PolicyQueueContextProps>({
    activeIndex: 0,
    attemptHistory: [],
    attemptReasons: [],
    attemptReasonOptions: [],
    bulkSearchActive: false,
    bulkSearchCategory: "",
    bulkSearchInput: "",
    carrierContacts: [],
    carrierRequests: [],
    comments: [],
    currentPolicyValue: defaultPolicyValue,
    currentPremiumPayment: defaultPremiumPayment,
    excelLoading: false,
    getPolicyDetail: notImplemented,
    gridData: [],
    handleSubmitPayment: notImplemented,
    handleSubmitValue: notImplemented,
    isAssignModalOpen: false,
    isAttemptModalOpen: false,
    isCarrierContactsModalOpen: false,
    isCommentModalOpen: false,
    policyDetail: undefined,
    policyDetailLoading: false,
    policyQueueData: undefined,
    policyQueueLoading: false,
    policyValueHistory: undefined,
    refreshCarrierRequests: notImplemented,
    premiumPaymentHistory: undefined,
    selectedCarrierRequest: undefined,
    selectedItems: undefined,
    selectedPremiumPaymentID: undefined,
    servicingUserOptions: [],
    servicingUsers: [],
    setActiveIndex: notImplemented,
    setAttemptHistory: notImplemented,
    setAttemptReasons: notImplemented,
    setAttemptReasonOptions: notImplemented,
    setBulkSearchActive: notImplemented,
    setBulkSearchCategory: notImplemented,
    setBulkSearchInput: notImplemented,
    setCarrierContacts: notImplemented,
    setCarrierRequests: notImplemented,
    setComments: notImplemented,
    setCurrentPolicyValue: notImplemented,
    setCurrentPremiumPayment: notImplemented,
    setExcelLoading: notImplemented,
    setGridData: notImplemented,
    setIsAssignModalOpen: notImplemented,
    setIsAttemptModalOpen: notImplemented,
    setIsCarrierContactsModalOpen: notImplemented,
    setIsCommentModalOpen: notImplemented,
    setPolicyDetail: notImplemented,
    setPolicyDetailLoading: notImplemented,
    setPolicyQueueData: notImplemented,
    setPolicyQueueLoading: notImplemented,
    setPolicyValueHistory: notImplemented,
    setPremiumPaymentHistory: notImplemented,
    setSelectedCarrierRequest: notImplemented,
    setSelectedItems: notImplemented,
    setSelectedPremiumPaymentID: notImplemented,
    setServicingUserOptions: notImplemented,
    setServicingUsers: notImplemented,
    setSort: notImplemented,
    setTextSearch: notImplemented,
    sort: [],
    switchboard: undefined,
    textSearch: ""
});

const attemptDateFields: (keyof Attempt)[] = ["CreatedDate"];
const carrierRequestDateFields: (keyof CarrierRequest)[] = ["CompletionDate", "MostRecentAttempt", "RequestDate"];
const policyDateFields: (keyof Policy)[] = [
    "AnnualStatementRequestDate",
    "EffectiveDate",
    "IllustrationRequestDate",
    "InsuredDOB",
    "LastAnnualStatementDate",
    "LastIllustrationDate",
    "LastTPADate",
    "PaymentHoldStartDate"
];
const policyValueDateFields: (keyof PolicyValue)[] = ["AsOfDate", "COIDate", "LapseDate", "PaidUntilDate"];
const premiumPaymentDateFields: (keyof PremiumPayment)[] = [
    "actual_date",
    "expected_date",
    "funding_date",
    "PaidUntilDate",
    "verification_date"
];

const PolicyQueueContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [attemptHistory, setAttemptHistory] = useState<Attempt[]>([]);
    const [attemptReasons, setAttemptReasons] = useState<AttemptReason[]>([]);
    const [attemptReasonOptions, setAttemptReasonOptions] = useState<DropdownItemProps[]>([]);
    const [bulkSearchActive, setBulkSearchActive] = useState<boolean>(false);
    const [bulkSearchCategory, setBulkSearchCategory] = useState<string>("PolicyID");
    const [bulkSearchInput, setBulkSearchInput] = useState<string>("");
    const [carrierContacts, setCarrierContacts] = useState<CarrierContact[] | undefined>();
    const [carrierRequests, setCarrierRequests] = useState<CarrierRequest[] | undefined>();
    const [comments, setComments] = useState<PolicyComment[]>([]);
    const [currentPolicyValue, setCurrentPolicyValue] = useState<PolicyValue>(defaultPolicyValue);
    const [currentPremiumPayment, setCurrentPremiumPayment] = useState<PremiumPayment>(defaultPremiumPayment);
    const [excelLoading, setExcelLoading] = useState<boolean>(false);
    const [gridData, setGridData] = useState<PolicyQueueRow[]>([]);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState<boolean>(false);
    const [isAttemptModalOpen, setIsAttemptModalOpen] = useState<boolean>(false);
    const [isCarrierContactsModalOpen, setIsCarrierContactsModalOpen] = useState<boolean>(false);
    const [isCommentModalOpen, setIsCommentModalOpen] = useState<boolean>(false);
    const [policyDetail, setPolicyDetail] = useState<Policy | undefined>();
    const [policyDetailLoading, setPolicyDetailLoading] = useState<boolean>(false);
    const [policyQueueData, setPolicyQueueData] = useState<PolicyQueueRow[]>();
    const [policyQueueLoading, setPolicyQueueLoading] = useState<boolean>(false);
    const [policyValueHistory, setPolicyValueHistory] = useState<PolicyValue[]>();
    const [premiumPaymentHistory, setPremiumPaymentHistory] = useState<PremiumPayment[]>();
    const [selectedCarrierRequest, setSelectedCarrierRequest] = useState<CarrierRequest>();
    const [selectedPremiumPaymentID, setSelectedPremiumPaymentID] = useState<number>();
    const [selectedItems, setSelectedItems] = useState<PolicyQueueRow[]>();
    const [servicingUserOptions, setServicingUserOptions] = useState<DropdownItemProps[]>([]);
    const [servicingUsers, setServicingUsers] = useState<ServicingUser[]>([]);
    const [sort, setSort] = useState<SortDescriptor[]>([]);
    const [textSearch, setTextSearch] = useState<string>("");
    const databroker = useDatabroker();
    const switchboard = useSwitchboard();

    useEffect(() => {
        switchboard.subscribe(subscription);
        console.log("Subscribed to Switchboard");
        return () => {
            switchboard.unsubscribe(subscription);
            console.log("Unsubscribed from Switchboard");
        };
    }, [switchboard]);

    const handleSubmitPayment = useCallback(
        (post: Post<PremiumPayment>) => {
            setGridData((prevGridData) =>
                prevGridData.filter((item) => item.CallTypeID === 1 || item.PremiumPaymentID !== post.data?.premiumpayment_id)
            );
            console.log(`Switchboard Event Received: Submit Payment for Premium Payment ID ${post.data?.premiumpayment_id}`);
        },
        [setGridData]
    );

    const handleSubmitValue = useCallback(
        (post: Post<PolicyValue>) => {
            setGridData((prevGridData) => prevGridData.filter((item) => item.PolicyID !== post.data?.PolicyId || item.CallTypeID !== 1));
            console.log(`Switchboard Event Received: Submit Value for Policy ID ${post.data?.PolicyId}`);
        },
        [setGridData]
    );

    useEffect(() => {
        switchboard.on(PolicyAction.SubmitPayment, (post: Post<PremiumPayment>) => handleSubmitPayment(post));
        switchboard.on(PolicyAction.SubmitValue, (post: Post<PolicyValue>) => handleSubmitValue(post));
    }, [switchboard, handleSubmitPayment, handleSubmitValue]);

    const getAttemptHistory = useCallback(
        async (policyID: number) => {
            try {
                const { error, data } = await databroker.sproc<Attempt>({
                    objectName: "Meddiag.PM.GetPolicyAttemptHistory",
                    parameters: {
                        PolicyID: policyID
                    }
                });
                if (error || !data) {
                    throw new Error(error);
                }
                return data;
            } catch (error) {
                console.log(error);
                toast.error("Error Retrieving Data");
            }
        },
        [databroker]
    );

    const getAttemptReasons = useCallback(async () => {
        try {
            const { error, data } = await databroker.search<AttemptReason>({
                objectName: "Meddiag.PM.PolicyAttemptReason_ref",
                orderBy: [["ReasonID", "ASC"]]
            });
            if (error || !data) {
                throw new Error(error);
            }
            return data;
        } catch (error) {
            console.log(error);
            toast.error("Error Retrieving Data");
        }
    }, [databroker]);

    const getCarrierContacts = useCallback(
        async (carrierID: number) => {
            try {
                const { error, data } = await databroker.search<CarrierContact>({
                    objectName: "Meddiag.PM.CarrierContact",
                    where: {
                        CompanyID: carrierID,
                        active: true
                    }
                });
                if (error || !data) {
                    throw new Error(error);
                }
                return data;
            } catch (error) {
                console.log(error);
                toast.error("Error Retrieving Data");
            }
        },
        [databroker]
    );

    const getCarriers = useCallback(async () => {
        try {
            const { error, data } = await databroker.search<Carrier>({
                objectName: "meddiag.PM.Company",
                select: ["CompanyID", "CompanyName"],
                where: { Active: true }
            });
            if (error || !data) {
                throw new Error(error);
            }
            return data;
        } catch (error) {
            console.log(error);
            toast.error("Error Retrieving Data");
        }
    }, [databroker]);

    const getCarrierRequests = useCallback(
        async (policyID: number) => {
            try {
                const { error, data } = await databroker.sproc<CarrierRequest>({
                    objectName: "Meddiag.PM.GetCarrierRequestsByPolicyID",
                    parameters: {
                        PolicyID: policyID
                    }
                });
                if (error || !data) {
                    throw new Error(error);
                }
                return data;
            } catch (error) {
                console.log(error);
                toast.error("Error Retrieving Data");
            }
        },
        [databroker]
    );

    const getComments = useCallback(
        async (policyID: number): Promise<PolicyComment[] | undefined> => {
            try {
                const { data, error } = await databroker.query({
                    select: ["c.comment_id", "c.object_type", "c.comment_date", "c.user", "c.comment", "c.type", "c.action"],
                    from: {
                        objectName: "MedDiag.PM.Comments",
                        as: "c"
                    },
                    where: {
                        object_id: policyID,
                        object_type: "Policy"
                    },
                    orderBy: [["comment_date", "DESC"]]
                });
                if (error || !data) {
                    throw new Error(error);
                }
                const tempData = parseDates(data, ["comment_date"], "TRIM Z");
                return tempData;
            } catch (error) {
                console.error(error);
                toast.error(`Error retrieving policy comments`);
            }
        },
        [databroker]
    );

    const getPolicyDetail = async (policyID: number) => {
        setPolicyDetailLoading(true);
        const [policyDetailData, carrierRequestData, commentData, policyValueData, premiumPaymentsData, attemptHistoryData] =
            await Promise.all([
                getPolicyDetailData(policyID),
                getCarrierRequests(policyID),
                getComments(policyID),
                getPolicyValueData(policyID),
                getPremiumPaymentData(policyID),
                getAttemptHistory(policyID)
            ]);
        const parsedPolicyDetailData = parseDates(policyDetailData || [], policyDateFields, "TRIM Z").map((item) => {
            const thisPolicy: Policy = {
                ...item,
                PolicyValueAssigneeUser: servicingUsers?.find((user) => user.UserID === item.PolicyValueAssigneeID) || null,
                PremiumPaymentAssigneeUser: servicingUsers?.find((user) => user.UserID === item.PremiumPaymentAssigneeID) || null,
                LoanPaymentAssigneeUser: servicingUsers?.find((user) => user.UserID === item.LoanPaymentAssigneeID) || null
            };
            return thisPolicy;
        });

        setPolicyDetail(parsedPolicyDetailData[0]);
        const parsedCarrierRequests = parseDates(carrierRequestData || [], carrierRequestDateFields, "TRIM Z").map((item) => {
            const thisCarrierRequest: CarrierRequest = { ...item, selected: false };
            return thisCarrierRequest;
        });
        setCarrierRequests(parsedCarrierRequests);
        const commentsWithUsers = commentData?.map((item) => {
            const thisComment = {
                ...item,
                servicing_user: servicingUsers?.find((user) => user.Username.toLowerCase() === item.user.toLowerCase()) || {
                    UserID: "",
                    Username: "",
                    FirstName: "",
                    LastName: "",
                    FullName: ""
                }
            };
            return thisComment;
        });
        setComments(commentsWithUsers || []);
        const carrierContactsData = await getCarrierContacts(parsedPolicyDetailData[0].CarrierID);
        setCarrierContacts(carrierContactsData);
        const parsedPolicyValueData = parseDates(policyValueData || [], policyValueDateFields, "TRIM Z");
        setPolicyValueHistory(parsedPolicyValueData);
        const parsedPremiumPaymentsData = parseDates(premiumPaymentsData || [], premiumPaymentDateFields, "TRIM Z");
        setPremiumPaymentHistory(parsedPremiumPaymentsData);
        const parsedAttemptHistoryData = parseDates(attemptHistoryData || [], attemptDateFields, "TRIM Z");
        setAttemptHistory(parsedAttemptHistoryData);
        setPolicyDetailLoading(false);
    };

    const getPolicyDetailData = useCallback(
        async (policyID: number) => {
            try {
                const { error, data } = await databroker.sproc<Policy>({
                    objectName: "Meddiag.PM.GetPolicyDetail",
                    parameters: {
                        PolicyID: policyID
                    }
                });
                if (error || !data) {
                    throw new Error(error);
                }
                return data;
            } catch (error) {
                console.log(error);
                toast.error("Error Retrieving Data");
            }
        },
        [databroker]
    );

    const getPolicyQueueData = useCallback(async () => {
        try {
            const { error, data } = await databroker.sproc<RawPolicyQueueRowData>({
                objectName: "Meddiag.PM.GetPolicyQueue"
            });
            if (error || !data) {
                throw new Error(error);
            }
            return data;
        } catch (error) {
            console.log(error);
            toast.error("Error Retrieving Data");
        }
    }, [databroker]);

    const getPolicyValueData = useCallback(
        async (policyID: number) => {
            try {
                const { error, data } = await databroker.search<PolicyValue>({
                    objectName: "Meddiag.PM.PolicyValueHistory",
                    where: {
                        PolicyId: policyID
                    },
                    orderBy: [["AsOfDate", "DESC"]]
                });
                if (error || !data) {
                    throw new Error(error);
                }
                return data;
            } catch (error) {
                console.log(error);
                toast.error("Error Retrieving Data");
            }
        },
        [databroker]
    );

    const getPortfolios = useCallback(async () => {
        try {
            const { error, data } = await databroker.search<Portfolio>({
                objectName: "meddiag.PM.Portfolio",
                select: ["PortfolioID", "PortfolioName", "CaseManagerGuid"]
            });
            if (error || !data) {
                throw new Error(error);
            }
            return data;
        } catch (error) {
            console.log(error);
            toast.error("Error Retrieving Data");
        }
    }, [databroker]);

    const getPremiumPaymentData = useCallback(
        async (policyID: number) => {
            try {
                const { error, data } = await databroker.sproc<PremiumPayment>({
                    objectName: "Meddiag.PM.GetPremiumPaymentHistory",
                    parameters: {
                        PolicyID: policyID
                    }
                });
                if (error || !data) {
                    throw new Error(error);
                }
                return data;
            } catch (error) {
                console.log(error);
                toast.error("Error Retrieving Data");
            }
        },
        [databroker]
    );

    const getUsers = useCallback(async () => {
        try {
            const { error, data } = await databroker.sproc<ServicingUser>({
                objectName: "MedDiag.dbo.GetUsersInRole",
                parameters: { Role: "Servicing" }
            });
            if (error || !data) {
                throw new Error(error);
            }
            return data;
        } catch (error) {
            console.error(error);
            toast.error(`Error getting Users - ${error}`);
        }
    }, [databroker]);

    const refreshCarrierRequests = useCallback(
        async (policyID: number) => {
            const carrierRequestData = await getCarrierRequests(policyID);
            const parsedCarrierRequests = parseDates(carrierRequestData || [], carrierRequestDateFields, "TRIM Z").map((item) => {
                const thisCarrierRequest: CarrierRequest = { ...item, selected: false };
                return thisCarrierRequest;
            });
            setCarrierRequests(parsedCarrierRequests);
        },
        [setCarrierRequests, getCarrierRequests]
    );

    const manageUpcomingCalls = (policyQueueData: PolicyQueueRow[]) => {
        const upcomingCallPolicyIDs = policyQueueData.filter((item) => item.DaysPastDue < 0).map((item) => item.PolicyID);
        const filteredPolicyQueueData = policyQueueData
            .filter((item) => item.DaysPastDue >= 0)
            .map((item) => {
                return { ...item, upcomingCall: upcomingCallPolicyIDs.includes(item.PolicyID) };
            });
        return filteredPolicyQueueData;
    };

    const getClients = useCallback(async () => {
        try {
            const { error, data } = await databroker.search<{ ClientID: number; Name: string }>({
                objectName: "meddiag.dbo.tClient",
                select: ["ClientID", "Name"]
            });
            if (error || !data) {
                throw new Error(error);
            }
            return data;
        } catch (error) {
            console.log(error);
            toast.error("Error Retrieving Data");
        }
    }, [databroker]);

    useEffect(() => {
        (async () => {
            setPolicyQueueLoading(true);
            setPolicyDetailLoading(true);
            const [users, attemptReasonData] = await Promise.all([getUsers(), getAttemptReasons()]);
            const usersWithFullName = users?.map((item) => {
                const thisUser = { ...item, FullName: item.FirstName + " " + item.LastName };
                return thisUser;
            });
            setServicingUsers(usersWithFullName || []);
            const options = lodashOrderBy(
                usersWithFullName?.map((item) => {
                    return {
                        text: item.FullName,
                        value: item.UserID,
                        key: item.UserID
                    };
                }),
                ["text"],
                ["asc"]
            );
            setServicingUserOptions(options);
            setAttemptReasons(attemptReasonData || []);
            const attemptOptions = attemptReasonData?.map((item) => {
                return {
                    text: item.Name,
                    value: item.ReasonID,
                    key: item.ReasonID
                };
            });
            setAttemptReasonOptions(attemptOptions || []);
            const [carriersData, portfoliosData, clientData, rawPolicyQueueData] = await Promise.all([
                getCarriers(),
                getPortfolios(),
                getClients(),
                getPolicyQueueData()
            ]);
            setPolicyDetailLoading(false);
            const cleanPolicyQueueData = rawPolicyQueueData?.map((item) => {
                const carrierID = item.CID;
                const portfolioID = item.PTID;
                const clientID = item.CLID;
                const portfolioManagerID = portfoliosData?.find((item) => item.PortfolioID === portfolioID)?.CaseManagerGuid || "";
                const thisPolicy: PolicyQueueRow = {
                    AssignedTo: item.AT ? usersWithFullName?.find((user) => user.Username.toLowerCase() === item.AT) || null : null,
                    CallStatus: item.CS,
                    CallTypeID: item.CT,
                    CallType: item.CT === 1 ? "Value" : item.CT === 2 ? "Premium" : "Loan",
                    CarrierID: carrierID,
                    CarrierName: carriersData?.find((item) => item.CompanyID === carrierID)?.CompanyName.replace(/&amp;/, "&") || "",
                    ClientName: clientData?.find((data) => data.ClientID === clientID)?.Name.replace(/&amp;/, "&") || "",
                    DaysPastDue: item.DPD,
                    DoNotCall: item.DNC,
                    IsBulkValues: item.IBV,
                    InsuredID: item.IID,
                    InsuredLastName: item.ILN,
                    OwnerID: item.OID,
                    PolicyID: item.PID,
                    PolicyNumber: item.PN,
                    PortfolioID: item.PTID,
                    PortfolioName: portfoliosData?.find((item) => item.PortfolioID === portfolioID)?.PortfolioName || "",
                    PortfolioManager: usersWithFullName?.find((item) => item.UserID === portfolioManagerID) || null,
                    PremiumPaymentID: item.PPID,
                    selected: false
                };
                return thisPolicy;
            });
            setPolicyQueueData(manageUpcomingCalls(cleanPolicyQueueData || []));
            setPolicyQueueLoading(false);
        })();
    }, [getCarriers, getPortfolios, getPolicyQueueData, getUsers, getAttemptReasons, getClients]);

    useEffect(() => setGridData(policyQueueData || []), [policyQueueData]);

    return (
        <PolicyQueueContext.Provider
            value={{
                activeIndex,
                attemptHistory,
                attemptReasons,
                attemptReasonOptions,
                bulkSearchActive,
                bulkSearchCategory,
                bulkSearchInput,
                carrierContacts,
                carrierRequests,
                comments,
                currentPolicyValue,
                currentPremiumPayment,
                excelLoading,
                getPolicyDetail,
                gridData,
                handleSubmitPayment,
                handleSubmitValue,
                isAssignModalOpen,
                isAttemptModalOpen,
                isCarrierContactsModalOpen,
                isCommentModalOpen,
                policyDetail,
                policyDetailLoading,
                policyQueueData,
                policyQueueLoading,
                policyValueHistory,
                premiumPaymentHistory,
                refreshCarrierRequests,
                selectedCarrierRequest,
                selectedItems,
                selectedPremiumPaymentID,
                servicingUserOptions,
                servicingUsers,
                setActiveIndex,
                setAttemptHistory,
                setAttemptReasons,
                setAttemptReasonOptions,
                setBulkSearchActive,
                setBulkSearchCategory,
                setBulkSearchInput,
                setCarrierContacts,
                setCarrierRequests,
                setComments,
                setCurrentPolicyValue,
                setCurrentPremiumPayment,
                setExcelLoading,
                setGridData,
                setIsAssignModalOpen,
                setIsAttemptModalOpen,
                setIsCarrierContactsModalOpen,
                setIsCommentModalOpen,
                setPolicyDetail,
                setPolicyDetailLoading,
                setPolicyQueueData,
                setPolicyQueueLoading,
                setPolicyValueHistory,
                setPremiumPaymentHistory,
                setSelectedCarrierRequest,
                setSelectedItems,
                setSelectedPremiumPaymentID,
                setServicingUserOptions,
                setServicingUsers,
                setSort,
                setTextSearch,
                sort,
                switchboard,
                textSearch
            }}
        >
            {children}
        </PolicyQueueContext.Provider>
    );
};

export default PolicyQueueContextProvider;
